import React, { useEffect, useMemo, useRef, useState } from 'react';
import { willhabenBrokerAPI } from '../lib/api';
import { toast } from 'sonner';
import { useTranslation } from '../lib/i18n';
import { track } from '../lib/analytics';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import {
  Loader2, CheckCircle2, Search, Link as LinkIcon, ExternalLink,
  Users, Filter, X, AlertCircle, RefreshCw,
} from 'lucide-react';

/**
 * Three-state bulk-import flow for a willhaben broker (Anbieter):
 *   1. paste:     user pastes the broker URL
 *   2. select:    user picks which listings to import (and optionally
 *                 enables agent-grouping for big agencies)
 *   3. progress:  background job is running, we poll its status
 *
 * After the job finishes the parent's `onCompleted` callback fires
 * with summary stats so the parent (NewPropertyModal etc.) can close
 * the flow and refresh the property list.
 *
 * The agent-grouping is LAZY. Fetching CONTACT/NAME from each listing
 * costs ~1s per listing, so we only do it when the user explicitly
 * clicks "Nach Makler gruppieren". Many users (single-agent brokers)
 * never need it.
 */
export default function WillhabenBrokerImportFlow({ onCompleted, variant = 'card', seedUrl }) {
  const { t } = useTranslation();
  const [phase, setPhase] = useState('paste'); // paste | select | progress
  const [url, setUrl] = useState(seedUrl || '');
  const [loading, setLoading] = useState(false);

  // If parent handed us a seedUrl (e.g. user clicked "import more from
  // this broker" in the single-listing flow), kick off the preview
  // automatically so the user lands directly in the select phase.
  useEffect(() => {
    if (seedUrl && phase === 'paste') {
      // Defer to next tick so the form's url state is set first.
      const id = setTimeout(() => {
        if (url.trim() === seedUrl.trim()) {
          handlePreview();
        }
      }, 50);
      return () => clearTimeout(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seedUrl]);

  // After preview
  const [broker, setBroker] = useState(null);
  const [listings, setListings] = useState([]);
  const [selectedSet, setSelectedSet] = useState(new Set());

  // Filtering / grouping state
  const [filterCategory, setFilterCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [agentNames, setAgentNames] = useState({}); // {listing_id: agent_name}
  const [enrichLoading, setEnrichLoading] = useState(false);
  const [groupByAgent, setGroupByAgent] = useState(false);
  const [agentFilter, setAgentFilter] = useState('all'); // 'all' | agent_name | '_unknown'

  // Progress state
  const [jobId, setJobId] = useState(null);
  const [job, setJob] = useState(null);
  const pollRef = useRef(null);

  // Cleanup poll on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  // ─── Paste -> Preview ────────────────────────────────────────────
  const handlePreview = async (e) => {
    e?.preventDefault();
    if (!url.trim()) {
      toast.error(t('broker.needUrl') || 'Bitte eine Willhaben-Anbieter-URL einfügen.');
      return;
    }
    setLoading(true);
    try {
      const res = await willhabenBrokerAPI.preview(url.trim());
      setBroker(res.data.broker);
      const ls = res.data.listings || [];
      setListings(ls);
      // Default selection: every listing that's NOT already imported.
      const initial = new Set(ls.filter(l => !l.already_imported).map(l => l.listing_id));
      setSelectedSet(initial);
      setPhase('select');
      track('broker_previewed', {
        org_id: res.data.broker?.org_id,
        listing_count: ls.length,
      });
    } catch (err) {
      toast.error(
        err.response?.data?.detail
        || t('broker.previewFailed')
        || 'Anbieter-Daten konnten nicht geladen werden.'
      );
    } finally {
      setLoading(false);
    }
  };

  // ─── Selection helpers ───────────────────────────────────────────
  const toggleListing = (id) => {
    setSelectedSet(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const visibleListings = useMemo(() => {
    let out = listings;
    if (filterCategory !== 'all') {
      out = out.filter(l => l.category === filterCategory);
    }
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      out = out.filter(l =>
        (l.title || '').toLowerCase().includes(q)
        || (l.location || '').toLowerCase().includes(q)
      );
    }
    if (groupByAgent && agentFilter !== 'all') {
      out = out.filter(l => {
        const a = agentNames[l.listing_id];
        if (agentFilter === '_unknown') return !a;
        return a === agentFilter;
      });
    }
    return out;
  }, [listings, filterCategory, searchTerm, groupByAgent, agentFilter, agentNames]);

  const categories = useMemo(() => {
    const c = {};
    listings.forEach(l => { c[l.category] = (c[l.category] || 0) + 1; });
    return c;
  }, [listings]);

  const uniqueAgents = useMemo(() => {
    const set = new Set();
    Object.values(agentNames).forEach(n => { if (n) set.add(n); });
    return Array.from(set).sort();
  }, [agentNames]);

  const selectAllVisible = () => {
    setSelectedSet(prev => {
      const next = new Set(prev);
      visibleListings.forEach(l => {
        if (!l.already_imported) next.add(l.listing_id);
      });
      return next;
    });
  };
  // "Auswahl leeren" must always clear the WHOLE selection. Anything
  // else is confusing when filters are active. Earlier behavior was
  // "deselect visible only" which left hidden-but-still-selected items
  // counted in the import button, so the user thought nothing was
  // selected but the count said 12. Hard reset is the only sane option.
  const clearAllSelection = () => setSelectedSet(new Set());

  // ─── Lazy agent enrichment ───────────────────────────────────────
  const handleEnrichAgents = async () => {
    if (enrichLoading) return;
    setEnrichLoading(true);
    try {
      // Send the listings the backend needs to fetch detail pages for –
      // listing_id + detail_url is the minimum.
      const payload = listings.map(l => ({
        listing_id: l.listing_id,
        detail_url: l.detail_url,
      }));
      const res = await willhabenBrokerAPI.agents(payload);
      setAgentNames(res.data?.agents || {});
      setGroupByAgent(true);
      track('broker_agents_enriched', { listing_count: listings.length });
    } catch (err) {
      toast.error(
        err.response?.data?.detail
        || t('broker.enrichFailed')
        || 'Makler-Namen konnten nicht ermittelt werden.'
      );
    } finally {
      setEnrichLoading(false);
    }
  };

  // ─── Start the import job ────────────────────────────────────────
  const handleStartImport = async () => {
    const selected = listings.filter(l => selectedSet.has(l.listing_id) && !l.already_imported);
    if (!selected.length) {
      toast.error(t('broker.pickAtLeastOne') || 'Bitte mindestens ein Inserat auswählen.');
      return;
    }
    setLoading(true);
    try {
      const payload = selected.map(l => ({
        listing_id: l.listing_id,
        detail_url: l.detail_url,
      }));
      const res = await willhabenBrokerAPI.import(payload);
      setJobId(res.data.job_id);
      setPhase('progress');
      track('broker_import_started', {
        org_id: broker?.org_id,
        listing_count: selected.length,
      });
      // Start polling
      const tick = async () => {
        try {
          const r = await willhabenBrokerAPI.importStatus(res.data.job_id);
          setJob(r.data);
          if (['completed', 'cancelled', 'failed'].includes(r.data.status)) {
            if (pollRef.current) clearInterval(pollRef.current);
            track('broker_import_finished', {
              status: r.data.status,
              completed: r.data.completed,
              failed: r.data.failed,
            });
            // Notify parent so the property list refreshes etc.
            if (r.data.status === 'completed' && onCompleted) {
              setTimeout(() => onCompleted(r.data), 1500);
            }
          }
        } catch (e) {
          // Transient poll failure. Keep going.
        }
      };
      tick();
      pollRef.current = setInterval(tick, 3000);
    } catch (err) {
      toast.error(
        err.response?.data?.detail
        || t('broker.startFailed')
        || 'Import konnte nicht gestartet werden.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCancelJob = async () => {
    if (!jobId) return;
    try {
      await willhabenBrokerAPI.importCancel(jobId);
      toast.info(t('broker.cancelling') || 'Wird abgebrochen...');
    } catch (err) {
      // Job might have just finished. Silent
    }
  };

  const Wrapper = ({ children }) =>
    variant === 'embedded' ? (
      <div className="space-y-4">{children}</div>
    ) : (
      <Card className="glass border-[#1f4d3a]/30 mb-8 overflow-hidden">
        <CardContent className="p-5 sm:p-6 space-y-5">{children}</CardContent>
      </Card>
    );

  // ─── PHASE 1: paste URL ──────────────────────────────────────────
  if (phase === 'paste') {
    return (
      <Wrapper>
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-[#e9efe7] border border-[#1f4d3a]/30 shrink-0">
            <Users className="w-5 h-5 text-[#1f4d3a]" />
          </div>
          <div>
            <h3 className="font-semibold text-[#23211a] mb-1">
              {t('broker.title') || 'Alle Inserate vom Anbieter importieren'}
            </h3>
            <p className="text-xs text-[#6f6a5c] leading-relaxed">
              {t('broker.subtitle')
                || 'Füge den Link zur Willhaben-Anbieter-Seite ein. Z. B. die "Mehr von diesem Anbieter" Seite. Wir holen alle aktiven Inserate, du wählst aus welche du importierst.'}
            </p>
          </div>
        </div>

        <form onSubmit={handlePreview} className="space-y-3" data-testid="broker-form">
          <label className="text-sm font-medium text-[#23211a] block">
            {t('broker.urlLabel') || 'Anbieter-URL'}
          </label>
          <div className="flex gap-2 flex-col sm:flex-row">
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://www.willhaben.at/iad/immobilien/immobilienmakler/...?orgId=..."
              className="flex-1 bg-[#f7f3ea]/85 border border-[#e3dccb] rounded-md px-3 py-2 text-sm text-[#23211a] focus:outline-none focus:border-[#1f4d3a]"
              required
              disabled={loading}
              data-testid="broker-url-input"
            />
            <Button
              type="submit"
              disabled={loading || !url.trim()}
              className="bg-[#1f4d3a] hover:bg-[#163a2c] text-white disabled:bg-[#e9e2d2] disabled:text-[#8a8474] whitespace-nowrap"
              data-testid="broker-load-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
              {t('broker.loadBtn') || 'Anbieter laden'}
            </Button>
          </div>
          <p className="text-[11px] text-[#8a8474]">
            {t('broker.hint')
              || 'Tipp: Klick in einem deiner eigenen Willhaben-Inserate auf "Mehr von diesem Anbieter". Die geöffnete Seite ist die richtige URL.'}
          </p>
        </form>
      </Wrapper>
    );
  }

  // ─── PHASE 2: select listings ────────────────────────────────────
  if (phase === 'select') {
    const importableSelected = Array.from(selectedSet).filter(id => {
      const l = listings.find(x => x.listing_id === id);
      return l && !l.already_imported;
    }).length;
    // Count selected items that are currently HIDDEN by filters. We
    // surface this as a small warning so the user never gets caught
    // off-guard by an import button counting things they can't see.
    const visibleIds = new Set(visibleListings.map(l => l.listing_id));
    const hiddenSelected = Array.from(selectedSet).filter(id => {
      if (visibleIds.has(id)) return false;
      const l = listings.find(x => x.listing_id === id);
      return l && !l.already_imported;
    }).length;
    const hasActiveFilter =
      filterCategory !== 'all'
      || (searchTerm.trim().length > 0)
      || (groupByAgent && agentFilter !== 'all');

    return (
      <Wrapper>
        {/* Broker header */}
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3 min-w-0">
            {broker?.logo_url && (
              <img src={broker.logo_url} alt="" className="w-12 h-12 rounded-lg object-contain bg-[#fffdf7] border border-[#e3dccb] shrink-0" />
            )}
            <div className="min-w-0">
              <h3 className="font-semibold text-[#23211a] truncate">{broker?.org_name}</h3>
              <p className="text-xs text-[#6f6a5c]">
                {t('broker.activeCount', { count: listings.length }) || `${listings.length} aktive Inserate gefunden`}
                {broker?.address_town ? ` · ${broker.address_town}` : ''}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => { setPhase('paste'); setBroker(null); setListings([]); setSelectedSet(new Set()); }}
            className="text-xs text-[#6f6a5c] hover:text-[#23211a] underline shrink-0"
          >
            {t('broker.changeBroker') || 'Anderer Anbieter'}
          </button>
        </div>

        {/* Filter row */}
        <div className="space-y-2">
          <div className="flex flex-wrap gap-2">
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="bg-[#f7f3ea]/85 border border-[#e3dccb] rounded-md px-3 py-1.5 text-xs text-[#23211a] focus:outline-none focus:border-[#1f4d3a]"
            >
              <option value="all">{t('broker.allCategories') || 'Alle Kategorien'} ({listings.length})</option>
              {Object.entries(categories).map(([k, n]) => (
                <option key={k} value={k}>{k} ({n})</option>
              ))}
            </select>
            <input
              type="search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t('broker.searchPlaceholder') || 'Titel/Ort suchen...'}
              className="flex-1 min-w-[140px] bg-[#f7f3ea]/85 border border-[#e3dccb] rounded-md px-3 py-1.5 text-xs text-[#23211a] focus:outline-none focus:border-[#1f4d3a]"
            />
            {!groupByAgent ? (
              <button
                type="button"
                onClick={handleEnrichAgents}
                disabled={enrichLoading}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs text-[#1f4d3a] border border-[#1f4d3a]/30 hover:border-[#1f4d3a] disabled:opacity-50"
                data-testid="broker-enrich-agents"
              >
                {enrichLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Users className="w-3.5 h-3.5" />}
                {t('broker.groupByAgent') || 'Nach Makler gruppieren'}
              </button>
            ) : (
              <select
                value={agentFilter}
                onChange={(e) => setAgentFilter(e.target.value)}
                className="bg-[#f7f3ea]/85 border border-[#1f4d3a]/40 rounded-md px-3 py-1.5 text-xs text-[#23211a] focus:outline-none focus:border-[#1f4d3a]"
              >
                <option value="all">{t('broker.allAgents') || 'Alle Makler'} ({uniqueAgents.length})</option>
                {uniqueAgents.map(name => (
                  <option key={name} value={name}>{name}</option>
                ))}
                <option value="_unknown">{t('broker.unknownAgent') || '(Ohne Namen)'}</option>
              </select>
            )}
          </div>
          <div className="flex items-center gap-3 text-[11px] text-[#8a8474] flex-wrap">
            <span>
              {t('broker.selectedCount', { count: importableSelected }) || `${importableSelected} ausgewählt`}
            </span>
            <button onClick={selectAllVisible} className="text-[#1f4d3a] hover:text-[#1f4d3a] underline">
              {t('broker.selectAllVisible') || 'Alle (sichtbar) auswählen'}
            </button>
            <button onClick={clearAllSelection} className="text-[#6f6a5c] hover:text-[#23211a] underline">
              {t('broker.clearAll') || 'Auswahl leeren'}
            </button>
          </div>
          {/* Filter-aware warning: when a filter is active AND the user
              has selected items currently hidden by that filter, tell
              them so the import-count number isn't a surprise. */}
          {hasActiveFilter && hiddenSelected > 0 && (
            <div className="flex items-start gap-2 text-[11px] text-[#9a6d1f] bg-[#f6edd8] border border-[#c7a869] rounded-md px-2.5 py-1.5">
              <AlertCircle className="w-3.5 h-3.5 mt-px shrink-0" />
              <span>
                {t('broker.hiddenSelectedHint', { count: hiddenSelected })
                  || `${hiddenSelected} weitere ausgewählte Inserate sind durch den Filter ausgeblendet. Sie zählen mit beim Import.`}
                {' '}
                <button
                  type="button"
                  onClick={() => {
                    setFilterCategory('all');
                    setSearchTerm('');
                    if (groupByAgent) setAgentFilter('all');
                  }}
                  className="underline text-[#9a6d1f] hover:text-[#7a5619]"
                >
                  {t('broker.clearFilters') || 'Filter zurücksetzen'}
                </button>
              </span>
            </div>
          )}
        </div>

        {/* Listings grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[60vh] overflow-y-auto pr-1">
          {visibleListings.map((l) => {
            const selected = selectedSet.has(l.listing_id);
            const disabled = l.already_imported;
            return (
              <button
                key={l.listing_id}
                type="button"
                onClick={() => !disabled && toggleListing(l.listing_id)}
                className={`relative flex gap-3 text-left p-3 rounded-lg border-2 transition ${
                  disabled
                    ? 'border-[#e3dccb] bg-[#f2ecdf] opacity-60 cursor-not-allowed'
                    : selected
                      ? 'border-[#1f4d3a] bg-[#f0f3ec] shadow-md'
                      : 'border-[#e3dccb] hover:border-[#c9c1ac] cursor-pointer'
                }`}
                data-testid={`broker-listing-${l.listing_id}`}
              >
                <div className="relative w-20 h-20 shrink-0 rounded bg-[#fffdf7] overflow-hidden">
                  {l.thumbnail ? (
                    <img src={l.thumbnail} alt="" className="w-full h-full object-cover" />
                  ) : null}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2 mb-0.5">
                    <h4 className="text-xs font-medium text-[#23211a] line-clamp-2">{l.title}</h4>
                    {selected && !disabled && (
                      <CheckCircle2 className="w-4 h-4 text-[#1f4d3a] shrink-0" />
                    )}
                  </div>
                  <p className="text-xs text-[#1f4d3a] font-semibold">{l.price}</p>
                  <p className="text-[11px] text-[#6f6a5c] truncate">{l.location}</p>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {l.property_type && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded bg-[#e9e2d2] text-[#4a463b] border border-[#d8d0bd]">
                        {l.property_type}
                      </span>
                    )}
                    {l.area_m2 && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded bg-[#e9e2d2] text-[#4a463b] border border-[#d8d0bd]">
                        {l.area_m2} m²
                      </span>
                    )}
                    {groupByAgent && agentNames[l.listing_id] && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded bg-[#e9efe7] text-[#1f4d3a] border border-[#1f4d3a]/30">
                        {agentNames[l.listing_id]}
                      </span>
                    )}
                  </div>
                  {disabled && (
                    <p className="text-[10px] text-[#1e7a4f] mt-1 inline-flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" />
                      {t('broker.alreadyImported') || 'Bereits importiert'}
                    </p>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        {visibleListings.length === 0 && (
          <p className="text-center text-sm text-[#8a8474] py-8">
            {t('broker.noMatch') || 'Keine Inserate passen zu den aktuellen Filtern.'}
          </p>
        )}

        {/* CTA */}
        <div className="flex gap-2 flex-col sm:flex-row pt-2 border-t border-[#e3dccb]">
          <Button
            onClick={handleStartImport}
            disabled={loading || importableSelected === 0}
            className="flex-1 bg-[#1f4d3a] hover:bg-[#163a2c] text-white disabled:bg-[#e9e2d2]"
            data-testid="broker-import-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
            {t('broker.startImport', { count: importableSelected })
              || `${importableSelected} Inserate importieren`}
          </Button>
        </div>

        <p className="text-[10px] text-[#8a8474] leading-relaxed">
          {t('broker.disclaimer')
            || 'Hinweis: Mit dem Import bestätigst du, dass du die Anzeigen erstellt hast oder vom Eigentümer beauftragt bist und die Bilder im Video verwenden darfst.'}
        </p>
      </Wrapper>
    );
  }

  // ─── PHASE 3: progress ───────────────────────────────────────────
  if (phase === 'progress') {
    const total = job?.total || 0;
    const completed = job?.completed || 0;
    const failed = job?.failed || 0;
    const pct = total ? Math.round(((completed + failed) / total) * 100) : 0;
    const isDone = job && ['completed', 'cancelled'].includes(job.status);

    return (
      <Wrapper>
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h3 className="font-semibold text-[#23211a]">
                {isDone
                  ? (job.status === 'cancelled'
                      ? (t('broker.cancelled') || 'Import abgebrochen')
                      : (t('broker.done') || 'Import abgeschlossen'))
                  : (t('broker.running') || 'Inserate werden importiert...')}
              </h3>
              <p className="text-xs text-[#6f6a5c] mt-0.5">
                {completed} / {total} fertig
                {failed > 0 ? ` · ${failed} ${t('broker.errored') || 'Fehler'}` : ''}
              </p>
            </div>
            {!isDone && (
              <button
                onClick={handleCancelJob}
                className="text-xs text-[#6f6a5c] hover:text-[#a34430] underline"
              >
                {t('broker.cancel') || 'Abbrechen'}
              </button>
            )}
          </div>

          {/* Progress bar */}
          <div className="h-2 bg-[#e9e2d2] rounded-full overflow-hidden">
            <div
              className={`h-full transition-all ${isDone ? 'bg-[#1e7a4f]' : 'bg-[#1f4d3a]'}`}
              style={{ width: `${pct}%` }}
            />
          </div>

          {/* Per-listing results */}
          {job?.results?.length > 0 && (
            <div className="max-h-[40vh] overflow-y-auto pr-1 space-y-1 text-xs">
              {job.results.slice(-30).reverse().map((r, i) => (
                <div
                  key={i}
                  className={`flex items-center gap-2 px-2 py-1 rounded ${
                    r.status === 'imported' ? 'bg-[#1e7a4f]/5 text-[#1e7a4f]'
                    : r.status === 'skipped' ? 'bg-[#f2ecdf] text-[#6f6a5c]'
                    : 'bg-[#b3543f]/5 text-[#a34430]'
                  }`}
                >
                  {r.status === 'imported' && <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />}
                  {r.status === 'skipped' && <RefreshCw className="w-3.5 h-3.5 shrink-0" />}
                  {r.status === 'failed' && <AlertCircle className="w-3.5 h-3.5 shrink-0" />}
                  <span className="truncate flex-1">
                    {r.title || r.listing_id}
                    {r.status === 'failed' && r.error ? ` – ${r.error}` : ''}
                    {r.status === 'skipped' && r.reason === 'already_imported' ? ` – ${t('broker.alreadyImported') || 'bereits importiert'}` : ''}
                  </span>
                </div>
              ))}
            </div>
          )}

          {isDone && (
            <p className="text-xs text-[#6f6a5c] leading-relaxed pt-2 border-t border-[#e3dccb]">
              {job.status === 'completed'
                ? (t('broker.doneHint', { count: completed }) || `${completed} Inserate sind jetzt in deiner Liste. Du kannst sofort Videos rendern.`)
                : (t('broker.cancelledHint') || 'Bereits importierte Inserate bleiben gespeichert.')}
            </p>
          )}
        </div>
      </Wrapper>
    );
  }

  return null;
}
